import { useCallback, useEffect, useState } from 'react';
import { Handshake } from 'lucide-react';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { EntityPicker } from '@/components/EntityPicker';

/**
 * Eine Firma zum Kunden befördern.
 *
 * Aufrufbar von der Firmenakte (dann steht `organizationId` fest) und vom
 * Kontakt (dann kommt `contact` mit). Hängt der Kontakt an keiner Firma,
 * verlangt der Dialog zuerst eine: ein Kunde ist immer eine Firma, sonst
 * müsste jede Abfrage im Backend zwei Fälle kennen.
 */
interface Props {
  organizationId?: string;
  contact?: ContactRow;
  onClose: () => void;
  onDone: (customerId: string) => void;
}

export function PromoteCustomerDialog({ organizationId, contact, onClose, onDone }: Props) {
  const [firmaId, setFirmaId] = useState<string | null>(
    organizationId ?? contact?.organizationId ?? null,
  );
  const [neueFirma, setNeueFirma] = useState(
    contact && !contact.organizationId ? contact.displayName : '',
  );
  const [modus, setModus] = useState<'waehlen' | 'anlegen'>(
    organizationId || contact?.organizationId ? 'waehlen' : 'anlegen',
  );
  const [kontakte, setKontakte] = useState<ContactRow[]>([]);
  const [hauptkontakt, setHauptkontakt] = useState<string>(contact?.id ?? '');
  const [seit, setSeit] = useState(new Date().toISOString().slice(0, 10));
  const [projektTitel, setProjektTitel] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  useEffect(() => {
    if (!firmaId) {
      setKontakte([]);
      return;
    }
    void crm.contacts.list({ organizationId: firmaId }).then(setKontakte);
  }, [firmaId]);

  const speichern = useCallback(async () => {
    setBusy(true);
    setFehler(null);
    try {
      let zielFirma = firmaId;

      if (modus === 'anlegen') {
        const name = neueFirma.trim();
        if (!name) throw new Error('Die Firma braucht einen Namen.');
        if (!contact) throw new Error('Ohne Kontakt fehlt die Zielgruppe.');
        const firma = await crm.organizations.create({
          audienceId: contact.audienceId,
          name,
        });
        zielFirma = firma.id;
      }

      if (!zielFirma) throw new Error('Bitte eine Firma wählen.');

      // Die eigentliche Regel: Der Hauptansprechpartner eines Kunden muss zu
      // dessen Firma gehören — sonst leitet sich `isCustomer` beim Kontakt
      // aus der falschen (oder keiner) `organizationId` ab, und Kunde-Badge
      // wie Ansprechpartner-Liste liegen daneben. Der Vergleich deckt alle
      // drei Fälle in einem ab: keine Firma bisher → anhängen; neu angelegte
      // Firma → anhängen; eine andere vorhandene Firma gewählt → umhängen.
      // Vor dem Befördern, damit ein Fehler hier nicht eine Firma ohne
      // passenden Kontakt zurücklässt, während der Kunde schon existiert.
      if (contact && contact.organizationId !== zielFirma) {
        await crm.contacts.setOrganization(contact.id, zielFirma);
      }

      const kunde = await crm.customers.promote({
        organizationId: zielFirma,
        primaryContactId: hauptkontakt || (contact?.id ?? null),
        since: seit,
        firstProjectTitle: projektTitel.trim() || null,
      });
      onDone(kunde.id);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Befördern fehlgeschlagen');
      setBusy(false);
    }
  }, [contact, firmaId, hauptkontakt, modus, neueFirma, onDone, projektTitel, seit]);

  // Für den Hinweis reicht der Vergleich mit dem gerade gewählten Ziel: bei
  // „anlegen“ ist das Ziel per Definition eine andere Firma als die
  // vorhandene, bei „waehlen“ die gerade ausgewählte. Zeigt nur an, was
  // `speichern()` ohnehin täte — blockiert nichts, denn ein Umhängen kann
  // genau die Korrektur sein, die der Nutzer will.
  const firmaWechselt =
    contact?.organizationId != null &&
    (modus === 'anlegen' || (modus === 'waehlen' && firmaId !== contact.organizationId));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Handshake className="h-5 w-5 text-muted-foreground" />
          Zum Kunden befördern
        </h2>

        {contact && !contact.organizationId && (
          <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
            {contact.displayName} hängt an keiner Firma. Ein Kunde ist immer eine
            Firma — wähle eine vorhandene oder lege sie hier an.
          </p>
        )}

        {!organizationId && (
          <div className="space-y-2">
            <div className="flex gap-2 text-sm">
              <button
                type="button"
                onClick={() => setModus('waehlen')}
                className={modus === 'waehlen' ? 'font-semibold' : 'text-muted-foreground'}
              >
                Vorhandene Firma
              </button>
              <span className="text-muted-foreground">·</span>
              <button
                type="button"
                onClick={() => setModus('anlegen')}
                className={modus === 'anlegen' ? 'font-semibold' : 'text-muted-foreground'}
              >
                Neu anlegen
              </button>
            </div>

            {modus === 'waehlen' ? (
              <EntityPicker
                kind="organization"
                value={firmaId}
                onChange={setFirmaId}
                {...(contact ? { audienceId: contact.audienceId } : {})}
              />
            ) : (
              <Input
                value={neueFirma}
                onChange={(e) => setNeueFirma(e.target.value)}
                placeholder="Name der Firma"
              />
            )}
          </div>
        )}

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Hauptansprechpartner</span>
          <Select
            value={hauptkontakt}
            onChange={(e) => setHauptkontakt(e.target.value)}
            disabled={kontakte.length === 0}
          >
            <option value="">— keiner —</option>
            {kontakte.map((k) => (
              <option key={k.id} value={k.id}>
                {k.displayName}
              </option>
            ))}
          </Select>
        </label>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Kunde seit</span>
          <Input type="date" value={seit} onChange={(e) => setSeit(e.target.value)} />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Erstes Projekt (optional)</span>
          <Input
            value={projektTitel}
            onChange={(e) => setProjektTitel(e.target.value)}
            placeholder="z. B. Abschlussfotos 2027"
          />
        </label>

        {firmaWechselt && contact && (
          <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
            {contact.displayName} gehört derzeit zu {contact.organizationName} und
            wird dieser Firma zugeordnet.
          </p>
        )}

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void speichern()} disabled={busy}>
            {busy ? 'Befördere …' : 'Befördern'}
          </Button>
        </div>
      </div>
    </div>
  );
}
